import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { cn } from '@sandtech-hub-monorepo/ui-kit';

import { Module, sizeLabel } from '../../data/programs';
import { ActivityRow } from './ActivityRow';

interface Props {
  index: number;
  module: Module;
  defaultOpen?: boolean;
  /** Whether paid activities are currently openable (a covering subscription). */
  paidUnlocked?: boolean;
  /** The course's Savanna curriculum id, passed down to the activity links. */
  curriculumId: string;
}

/**
 * One module of a course, opening onto its activities.
 *
 * The interactive counterpart of `ModuleRow`. Same row - number, title, size,
 * progress - but it opens, and what it opens onto is activities with links
 * rather than a badge saying what the module costs, because an enrolled
 * learner is working through the content rather than weighing it up.
 *
 * Two levels of opening and no more: the course, then the module. The
 * activities sit directly inside, indented to the module's own title so the
 * nesting reads without a third thing to click.
 *
 * Progress is counted in activities, matching the course header, so a
 * part-finished module shows as part-finished rather than rounding to zero.
 */
export const InteractiveModuleRow = ({
  index,
  module,
  defaultOpen = false,
  paidUnlocked = false,
  curriculumId,
}: Props) => {
  const { activities } = module;
  const canOpen = activities.length > 0;
  const [open, setOpen] = useState(defaultOpen && canOpen);

  const passed = activities.filter((activity) => activity.completed).length;
  // Only meaningful for a flow Savanna resolved for a learner, which is the
  // only case this tree renders in.
  const showProgress = module.completed !== undefined && canOpen;
  const size = sizeLabel(module);

  return (
    <div>
      <button
        type="button"
        disabled={!canOpen}
        onClick={() => setOpen((isOpen) => !isOpen)}
        className={cn(
          'flex w-full items-center gap-3 py-2.5 text-left text-sm',
          canOpen && 'cursor-pointer'
        )}
      >
        <span className="w-5 shrink-0 text-muted-foreground">{index}.</span>
        <span className="min-w-0 flex-1 truncate text-foreground">
          {module.title}
        </span>

        {showProgress && (
          <span className="shrink-0 text-muted-foreground">
            {passed} of {activities.length} done
          </span>
        )}
        {!showProgress && size && (
          <span className="shrink-0 text-muted-foreground">{size}</span>
        )}

        {canOpen && (
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              open && 'rotate-180'
            )}
          />
        )}
      </button>

      {open && (
        <div className="pb-2 pl-8">
          {activities.map((activity) => (
            <ActivityRow
              key={activity.id}
              activity={activity}
              paidUnlocked={paidUnlocked}
              curriculumId={curriculumId}
            />
          ))}
        </div>
      )}
    </div>
  );
};
